import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import qs from 'query-string';

import {
    useAddIndirectSubscriptionMutation,
    useGetShareCategoriesQuery,
    useRequestProductDistributionMutation,
} from 'api/main';
import { currentInvestmentFundSet } from 'reducers/tunnel';
import { RoleNames, conventionStatus, queryStringifyOptions } from 'utils/constants';
import { useDialog, useMatchAtLeastOneRole, useSmallScreen } from 'utils/hooks';
import { jsonParseSafe } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import SubscriptionStartDialog from 'containers/tunnel/tunnel-entry/common-components/SubscriptionStartDialog';
import CreateSubscriptionDialog from 'containers/subscriptions/CreateSubscriptionDialog';
import { ActionButton, ConfirmDialog, ShareCategoryDialog } from 'components';
import { Box, Link, Typography } from '@mui/material';

const linkSx = isTablet => ({
    fontSize: isTablet ? '13px' : '15px',
});

const buttonSx = isTablet => ({
    minWidth: 0,
    width: '88%',
    borderRadius: '3px',
    fontSize: isTablet ? '10px' : '12px',
    fontWeight: 'bold',
});

const ConventionButton = ({ convention, fundId }) => {
    const isTablet = useSmallScreen('tablet');
    const isAdminCIF = useMatchAtLeastOneRole([RoleNames.ADMIN_CIF]);
    const [requestProductDistribution, { isLoading, isSuccess }] =
        useRequestProductDistributionMutation();

    const handleSignConvention = useCallback(() => {
        if (convention?.signatureUrls) {
            const signatureObjects = jsonParseSafe(convention.signatureUrls);
            if (signatureObjects?.signerUrl) {
                window.open(signatureObjects.signerUrl);
            }
        } else {
            requestProductDistribution({ id: fundId, withSignature: true });
        }
    }, [requestProductDistribution, fundId, convention]);

    useEffect(() => {
        if (isSuccess && convention && isAdminCIF) {
            const signatureObjects = jsonParseSafe(convention.signatureUrls);
            if (signatureObjects?.signerUrl) {
                window.open(signatureObjects.signerUrl);
            }
        }
    }, [convention, isSuccess, isAdminCIF]);

    if (!isAdminCIF) return null;

    return (
        <ActionButton
            sx={{
                ...buttonSx(isTablet),
                ...(convention?.status === conventionStatus.PARTIALLY_SIGNED
                    ? { backgroundColor: 'grey.600', color: 'common.white', pointerEvents: 'none' }
                    : {}),
            }}
            size="small"
            variant="contained"
            onClick={handleSignConvention}
            loading={isLoading}
        >
            {convention?.status === conventionStatus.PARTIALLY_SIGNED
                ? texts.AWAITING_SERVICER_SIGNATURE
                : texts.SIGN_CONVENTION}
        </ActionButton>
    );
};

ConventionButton.propTypes = {
    convention: PropTypes.object,
    fundId: PropTypes.number,
};

const CommercialisedProductCard = ({ fund, status, company = { conventions: [] } }) => {
    const subscriberId = useRef();
    const subscriberEmail = useRef(); // if the user does not exist

    const isTablet = useSmallScreen('tablet');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [createSubscriptionOpen, setCreateSubscriptionOpen] = useState(false);
    const [shareCategoryDialogOpen, setShareCategoryDialogOpen] = useState(false);
    const [subscriptionStartDialogOpen, setSubscriptionStartDialogOpen] = useState(false);
    const { data: shareCategories = [] } = useGetShareCategoriesQuery(
        { investmentFund: fund?.id },
        { skip: !fund?.id }
    );
    const {
        open: alertTunnelChangesOpen,
        onClose: handleAlertTunnelChangesClose,
        onOpen: handleAlertTunnelChangesOpen,
    } = useDialog();

    const [
        addIndirectSubscription,
        { isLoading: addSubscriptionLoading, isSuccess: addSubscriptionSuccess },
    ] = useAddIndirectSubscriptionMutation();

    const handleCreateSubscription = useCallback(() => {
        handleAlertTunnelChangesClose();
        dispatch(currentInvestmentFundSet(fund));
        setCreateSubscriptionOpen(prevState => !prevState);
    }, [dispatch, fund, handleAlertTunnelChangesClose]);

    const handleConfirmShareCategory = useCallback(() => {
        setShareCategoryDialogOpen(false);
        setSubscriptionStartDialogOpen(true);
    }, []);

    const handleStartSubscription = useCallback(() => {
        if (subscriberId.current) {
            addIndirectSubscription({
                userId: subscriberId.current,
                categoryId: shareCategories?.[0]?.id,
            });
        } else {
            let queryObj = { product: fund.id, email: subscriberEmail.current };
            navigate(`/create-subscription?${qs.stringify(queryObj, queryStringifyOptions)}`);
        }
    }, [addIndirectSubscription, navigate, fund, shareCategories]);

    const handleIndirectSubscriptionFlow = useCallback(({ userId, email }) => {
        subscriberId.current = userId;
        subscriberEmail.current = email;
        setShareCategoryDialogOpen(true);
    }, []);

    const convention = useMemo(
        () => company?.conventions?.find(c => c.investmentFund.id === fund.id),
        [company.conventions, fund.id]
    );

    useEffect(() => {
        if (addSubscriptionSuccess) {
            subscriberId.current = null;
            setSubscriptionStartDialogOpen(false);
        }
    }, [addSubscriptionSuccess]);

    const textColor = fund.distributorImage?.url
        ? { color: 'common.white' }
        : { color: 'common.black' };

    return (
        <Box
            height="320px"
            display="flex"
            sx={{
                backgroundImage: `url(${fund.distributorImage?.url})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right',
                backgroundOrigin: 'content-box',
                backgroundSize: 'cover',
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexDirection="column"
                flex="0 1 45%"
            >
                <Typography
                    flex="0 1 45%"
                    align="left"
                    textTransform="uppercase"
                    padding={2}
                    fontWeight={600}
                    sx={textColor}
                >
                    {fund.name}
                </Typography>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    textAlign="left"
                    flex="1 1"
                    width="100%"
                    pb="45px"
                    pl="20px"
                >
                    {fund.brochureAMF?.url && (
                        <Link
                            target="_blank"
                            sx={{ ...linkSx(isTablet), ...textColor }}
                            href={fund.brochureAMF.url}
                        >
                            {texts.BROCHURE_AMF}
                        </Link>
                    )}
                    {fund.advisorPresentation?.url && (
                        <Link
                            target="_blank"
                            sx={{ ...linkSx(isTablet), ...textColor }}
                            href={fund.advisorPresentation.url}
                        >
                            {texts.ADVISOR_PRESENTATION}
                        </Link>
                    )}
                    {fund.customerPresentation?.url && (
                        <Link
                            target="_blank"
                            sx={{ ...linkSx(isTablet), ...textColor }}
                            href={fund.customerPresentation.url}
                        >
                            {texts.CUSTOMER_PRESENTATION}
                        </Link>
                    )}
                    {fund.distributorArgument?.url && (
                        <Link
                            target="_blank"
                            sx={{ ...linkSx(isTablet), ...textColor }}
                            href={fund.distributorArgument.url}
                        >
                            {texts.CONSULTANT_ARGUMENT}
                        </Link>
                    )}
                    {fund.regulatoryPack?.url && (
                        <Link
                            target="_blank"
                            sx={{ ...linkSx(isTablet), ...textColor }}
                            href={fund.regulatoryPack?.url}
                        >
                            {texts.REGULATORY_PACK}
                        </Link>
                    )}
                    {fund.webinarRegistrationUrl && (
                        <Link
                            target="_blank"
                            sx={{ ...linkSx(isTablet), ...textColor }}
                            href={fund.webinarRegistrationUrl}
                        >
                            {texts.INSCRIPTION_WEBINAR}
                        </Link>
                    )}
                    {fund.replayWebinarUrl && (
                        <Link
                            target="_blank"
                            sx={{ ...linkSx(isTablet), ...textColor }}
                            href={fund.replayWebinarUrl}
                        >
                            {texts.REPLAY_WEBINAR}
                        </Link>
                    )}
                </Box>
            </Box>
            <Box
                flex="1 1"
                display="flex"
                pr="20px"
                pb="45px"
                alignItems="flex-end"
                flexDirection="column"
                justifyContent="flex-end"
            >
                {status === 'owned' ? (
                    <React.Fragment>
                        <ActionButton
                            variant="contained"
                            sx={{
                                ...buttonSx(isTablet),
                                paddingX: '5px',
                                paddingY: '3px',
                            }}
                            onClick={handleAlertTunnelChangesOpen}
                        >
                            {texts.CREATE_SUBSCRIPTION}
                        </ActionButton>
                        <ConfirmDialog
                            open={alertTunnelChangesOpen}
                            handleConfirm={handleCreateSubscription}
                            handleClose={handleCreateSubscription}
                            title={texts.TUNNEL_CHANGES_ALERT_TITLE}
                            confirmText={texts.CLOSE}
                            hideCancelButton
                        >
                            {texts.TUNNEL_CHANGES_ALERT_CONTENT.map(content => (
                                <Typography key={content} variant="body2" gutterBottom>
                                    {content}
                                </Typography>
                            ))}
                        </ConfirmDialog>
                        <CreateSubscriptionDialog
                            fundId={fund.id}
                            open={createSubscriptionOpen}
                            onClose={() => setCreateSubscriptionOpen(false)}
                            onExternalFlow={handleIndirectSubscriptionFlow}
                        />
                        <ShareCategoryDialog
                            open={shareCategoryDialogOpen}
                            onClose={() => setShareCategoryDialogOpen(false)}
                            onConfirm={handleConfirmShareCategory}
                        />
                        <SubscriptionStartDialog
                            open={subscriptionStartDialogOpen}
                            handleConfirm={handleStartSubscription}
                            loading={addSubscriptionLoading}
                        />
                    </React.Fragment>
                ) : (
                    !!fund.allowedSubscriptionCreation && (
                        <ConventionButton fundId={fund.id} convention={convention} />
                    )
                )}
            </Box>
        </Box>
    );
};

CommercialisedProductCard.propTypes = {
    company: PropTypes.object,
    fund: PropTypes.object.isRequired,
    status: PropTypes.oneOf(['owned', 'available']),
};

export default CommercialisedProductCard;
